
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExamType } from '@/services/db/types';
import { ExamFilter } from '@/utils/exam/types';
import { X, Filter as FilterIcon } from 'lucide-react';

const examTypes: { value: ExamType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'test', label: 'Tests' },
  { value: 'quiz', label: 'Quizzes' },
  { value: 'other', label: 'Other' },
];

interface ExamFiltersProps {
  filters: ExamFilter;
  onFilterChange: (filters: ExamFilter) => void;
  onClearFilters: () => void;
}

const ExamFilters: React.FC<ExamFiltersProps> = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}) => {
  const handleExamTypeChange = (value: string) => {
    onFilterChange({ 
      ...filters, 
      examType: value === 'all' ? undefined : value as ExamType
    });
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      subject: e.target.value || undefined
    });
  };

  const handleProgramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      programTitle: e.target.value || undefined
    });
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      dateFrom: e.target.value || undefined
    });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      dateTo: e.target.value || undefined
    });
  };
  
  const hasFilters = Object.values(filters).some(value => 
    value !== undefined && value !== ''
  );

  return (
    <div className="bg-background border rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Filter Results</h3>
        {hasFilters && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearFilters} 
            className="text-xs h-8 px-2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Clear all
          </Button>
        )}
      </div>
      <div className="space-y-4">
        <div>
          <Label htmlFor="examType" className="text-sm">Exam Type</Label>
          <Select
            value={filters.examType || 'all'}
            onValueChange={handleExamTypeChange}
          >
            <SelectTrigger id="examType">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              {examTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="subjectFilter" className="text-sm">Subject</Label>
          <Input
            id="subjectFilter"
            value={filters.subject || ''}
            onChange={handleSubjectChange}
            placeholder="Filter by subject"
          />
        </div>
        
        <div>
          <Label htmlFor="programFilter" className="text-sm">Program</Label>
          <Input
            id="programFilter"
            value={filters.programTitle || ''}
            onChange={handleProgramChange}
            placeholder="Filter by program"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="dateFrom" className="text-sm">From Date</Label>
            <Input
              id="dateFrom"
              type="date"
              value={typeof filters.dateFrom === 'string' ? filters.dateFrom : ''}
              onChange={handleDateFromChange}
            />
          </div>
          <div>
            <Label htmlFor="dateTo" className="text-sm">To Date</Label>
            <Input
              id="dateTo"
              type="date"
              value={typeof filters.dateTo === 'string' ? filters.dateTo : ''}
              onChange={handleDateToChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamFilters;
